import { normalizeAttitude, relativeCardTilt } from "./cardMotionMath";

const axes = ["x", "y", "z", "w"];
const MAX_INITIAL_CALIBRATION_MS = 700;
const MIN_INITIAL_SETTLE_MS = 350;
const distance = (a, b) => 2 * Math.acos(Math.min(1, Math.abs(axes.reduce((sum, key) => sum + a[key] * b[key], 0))));
const blend = (a, b, amount) => {
  const sign = axes.reduce((sum, key) => sum + a[key] * b[key], 0) < 0 ? -1 : 1;
  return normalizeAttitude(Object.fromEntries(axes.map((key) => [key, a[key] + (b[key] * sign - a[key]) * amount])));
};

// Calibrate from a settled pose, not the tap/opening motion or the first sensor
// frame. A stationary phone slowly becomes neutral again after changing grip;
// movement remains immediate and a held touch drag is never auto-centred.
export function createCardMotionCalibration() {
  let origin, startedAt, previousAt, anchor, stableSince, average, samples;
  function reset() {
    origin = anchor = average = null;
    startedAt = previousAt = stableSince = null;
    samples = 0;
  }
  reset();
  return {
    reset,
    sample(attitude, now, screenAngle = 0, interacting = false) {
      if (previousAt !== null && (now - previousAt > 500 || now < previousAt)) reset();
      const dt = previousAt === null ? 0 : now - previousAt;
      previousAt = now;
      if (startedAt === null) startedAt = now;
      // Compare against the start of the window, not just adjacent frames:
      // otherwise a slow deliberate turn would be mistaken for stillness.
      if (!anchor || interacting || distance(anchor, attitude) > 0.012) {
        anchor = average = attitude;
        stableSince = now;
        samples = 1;
      } else {
        samples += 1;
        average = blend(average, attitude, 1 / samples);
      }
      const settledFor = now - stableSince;
      if (!origin) {
        // Do not wait forever for a perfectly still phone. When the user is
        // already moving the card, use the current attitude as the neutral
        // reference after a short grace period; subsequent samples then drive
        // the tilt immediately. A genuinely still phone still gets the less
        // noisy averaged reference.
        const settled = settledFor >= MIN_INITIAL_SETTLE_MS && samples >= 6;
        const timedOut = now - startedAt >= MAX_INITIAL_CALIBRATION_MS;
        if (!interacting && (settled || timedOut)) origin = settled ? average : attitude;
        return { x: 0, y: 0 };
      }
      if (!interacting && settledFor >= 1800) {
        origin = blend(origin, average, 1 - Math.exp(-dt / 900));
      }
      return relativeCardTilt(origin, attitude, screenAngle);
    },
  };
}
